import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ChevronDown, Plus, X } from "lucide-react";

/**
 * Iter-180: Combobox for Client Group/Holding.
 *
 * Acts like a free-text input but also surfaces the existing list of groups under it
 * (filtered by typed text). Clicking an entry sets the value. Typing a new name and
 * blurring saves it — the parent's onChange receives the string, the backend
 * lazily registers it during the client save.
 *
 * Exposes a "Create '<name>'" affordance when the typed value has no match, so the
 * CEO can create a group up-front (POST /client-groups) and have it appear in other
 * dialogs immediately.
 */
export default function ClientGroupCombobox({ value, onChange, testid = "client-field-group" }) {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/client-groups");
      setGroups(r.data || []);
    } catch (e) {
      // silent — combobox still works as a free-text input.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const v = value || "";
  const filtered = useMemo(() => {
    const q = v.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, v]);

  const exactMatch = useMemo(
    () => groups.some((g) => g.name.toLowerCase() === v.trim().toLowerCase()),
    [groups, v]
  );

  const create = async () => {
    const name = v.trim();
    if (!name) return;
    try {
      await api.post("/client-groups", { name });
      toast.success(`Group "${name}" added`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create group");
    }
  };

  const clear = () => onChange("");

  return (
    <div ref={wrapperRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className="flex items-center gap-1">
        <Input
          data-testid={testid}
          value={v}
          placeholder={loading ? "Loading groups…" : "Type or select a group"}
          onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {v && (
          <button
            type="button"
            onClick={clear}
            title="Clear"
            data-testid={`${testid}-clear`}
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center text-zinc-500 hover:text-zinc-700"
          ><X size={14} /></button>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testid}-toggle`}
          className="shrink-0 w-9 h-9 inline-flex items-center justify-center text-zinc-500 hover:text-zinc-700 border border-zinc-300 rounded-md bg-white"
        ><ChevronDown size={14} /></button>
      </div>
      {open && (
        <div
          data-testid={`${testid}-list`}
          className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">No matching group yet.</div>
          )}
          {filtered.map((g) => (
            <button
              type="button"
              key={g.group_id || g.name}
              data-testid={`${testid}-opt-${g.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center justify-between"
              onClick={() => { onChange(g.name); setOpen(false); }}
            >
              <span className="text-sm text-zinc-800">{g.name}</span>
              {g.client_count > 0 && (
                <span className="text-[10px] font-mono text-zinc-400">{g.client_count} client{g.client_count > 1 ? "s" : ""}</span>
              )}
            </button>
          ))}
          {v.trim() && !exactMatch && (
            <button
              type="button"
              onClick={create}
              data-testid={`${testid}-create`}
              className="w-full text-left px-3 py-2 border-t border-zinc-100 hover:bg-brand-cream-50 flex items-center gap-2 text-brand-navy"
            >
              <Plus size={13} /> Create new group <span className="font-mono text-xs">"{v.trim()}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
